import { Component, Input, OnInit } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { ModalController } from '@ionic/angular';
import { PdfDocumentService } from '@services/pdf/pdf-document.service';

/**
 * Full page viewer for a pdf that already exists as binary data, with paging and
 * zoom. Used for documents the app does not render itself: patient attachments
 * and the stored proof of service document fetched from KEA.
 */
@Component({
  selector: 'itl-pdf-box',
  templateUrl: './pdf-box.component.html',
  styleUrls: ['./pdf-box.component.scss'],
  standalone: false,
})
export class PdfBoxComponent implements OnInit {
  @Input() public fileName: string;
  @Input() public src: ArrayBuffer;
  /**
   * Offers printing plus, depending on the platform, sharing (native) or
   * downloading (web).
   */
  @Input() public showActions = false;

  public currentPage = 1;
  public zoom = 1;

  public readonly minZoom = 0.5;
  public readonly maxZoom = 5;
  private readonly ZOOM_STEP = 0.25;

  private _showPrintButton = false;
  public get showPrintButton() {
    return this._showPrintButton;
  }
  private _showDownloadButton = false;
  public get showDownloadButton() {
    return this._showDownloadButton;
  }
  private _showShareButton = false;
  public get showShareButton() {
    return this._showShareButton;
  }
  private _pdfBytes: ArrayBuffer;

  constructor(
    private _modalCtrl: ModalController,
    private _pdfDocumentService: PdfDocumentService
  ) {}

  ngOnInit(): void {
    // pdf.js takes ownership of the buffer it renders and hands it back empty
    // (byteLength 0), so the actions keep their own copy of the bytes - taken
    // here, before the viewer in the template gets to see it
    this._pdfBytes = this.src?.byteLength ? this.src.slice(0) : this.src;
    this._showPrintButton = this.showActions;
    this._showShareButton = this.showActions && Capacitor.isNativePlatform();
    this._showDownloadButton = this.showActions && Capacitor.isNativePlatform() === false;
  }

  public zoomIn(): void {
    this.zoom = Math.min(this.zoom + this.ZOOM_STEP, this.maxZoom);
  }

  public zoomOut(): void {
    this.zoom = Math.max(this.zoom - this.ZOOM_STEP, this.minZoom);
  }

  public resetZoom(): void {
    this.zoom = 1;
  }

  public async print(): Promise<void> {
    try {
      await this._pdfDocumentService.print(this._pdfBytes, this.fileName);
    } catch (error) {
      window.logger.error('Pdf document could not be printed.', error);
    }
  }

  public async share(): Promise<void> {
    try {
      await this._pdfDocumentService.share(this._pdfBytes, this.fileName);
    } catch (error) {
      window.logger.error('Pdf document could not be shared.', error);
    }
  }

  public download(): void {
    this._pdfDocumentService.download(this._pdfBytes, this.fileName);
  }

  public async close(): Promise<void> {
    await this._modalCtrl.dismiss();
  }
}
